import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Permissions from "src/components/Permissions";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Title from "src/components/Title";
import { getTitleBudgetTypes } from "src/pages/FinancialManagement/interface";
import IModalRefPermissionsFinancialManagement, { FinancialManagementHierarchyPermissionsDTO, IModalPermissionsFinancialManagement } from "./interface";
import perimissionsFinancialManagementSchema from "./schema";

const ModalPermissionsFinancialManagement: React.ForwardRefRenderFunction<IModalRefPermissionsFinancialManagement, IModalPermissionsFinancialManagement> = ({
	type
}, ref) => {
	const { translate } = useTranslation(); 
	const { addToast } = RCCHookToast();
	const { handleSubmit, register, unregister, control, clearErrors, formState: { errors }, setError, reset } = useForm(); 
	const { setLoading } = useLoaderBackdrop();
    
	const budgetId = useRef<number>(0);
	const budgetPlanId = useRef<number>(0);
	const modalPermissions = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState("");
	const onCallbackFunction = useRef<Function>(() => {});
	const [permissions, setPermissions] = useState<FinancialManagementHierarchyPermissionsDTO>({} as FinancialManagementHierarchyPermissionsDTO);
	const openModalPermissionsFinancialManagement = async (id: number, subtitle: string, ofa001Id: number, onCallback?: Function) => {
		try{
			setLoading(true);
			reset();
			budgetId.current = id;
			budgetPlanId.current = ofa001Id;

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetPermissionsInHierarchy",// methodGetPermissions,
				urlParameters: {
					id,
					type
				}
			});
            
			setPermissions(data);

			setSubtitleModal(`${getTitleBudgetTypes(type, translate)  }: ${ subtitle}`);
			onCallbackFunction.current = onCallback || (() => {});
			
			setLoading(false);
			modalPermissions.current?.openModal();
		}catch (err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar permissões"), addToast);
		}
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			
			modalPermissions.current?.setLoadingButton();
			const dataForm = {...data};
			
			if(data.permissions && data.permissions.length > 0){
				dataForm.permissions = dataForm.permissions.map((permission: any) => {
					if(typeof permission.efa005Id === "object")
						return {
							...permission,
							efa005Id: permission.efa005Id.value
						};
					if(typeof permission.efa004Id === "object")
						return {
							...permission,
							efa004Id: permission.efa004Id.value
						};
					return permission;
				});
	
				await perimissionsFinancialManagementSchema(translate).validate({ ...dataForm }, {
					abortEarly: false
				});
		
				dataForm.permissions = dataForm.permissions.filter((dt: any) => dt.efa005Id !== 0);
				dataForm.permissions = dataForm.permissions.filter((dt: any) => dt.efa004Id !== 0);
			}

			await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertPermissionsInHierarchy",// methodInsertPermissions,
				body: {
					permissions: dataForm.permissions,
					id: budgetId.current,
					budgetPlanId: budgetPlanId.current,
					type
				}
			});

			addToast({
				color: "green",
				description: translate("Permissão alterada com sucesso"),
				title: translate("Sucesso")
			});

			onCallbackFunction.current();
			modalPermissions.current?.resetLoadingButton();
			modalPermissions.current?.closeModal();
		}catch(err){
			modalPermissions.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, translate("Erro ao salvar permissões"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalPermissionsFinancialManagement
	}));

	return (
		<Modal
			ref={modalPermissions}
			header={{
				title: translate("Permissões"),
				subtitle: subtitleModal,
				icon: {
					name: "lock"
				}
			}}
			onSubmit={handleSubmit(onSubmit)}
			styleInline={ModalSide}
		>
			<Title color="grey" size="xs" marginBottom={8}>
				{translate("Utilize as permissões para informar quais usuários, ou grupos de usuários, terão acesso ou permissão para editar")}
			</Title>
			<Permissions
				register={register}
				unregister={unregister}
				control={control}
				error={errors}
				clearErrors={clearErrors}
				permissionsNames= {{
					nameObject: "permissions",
					nameOfID: "id",
					nameOfSelectUser: "efa005Id",
					nameOfSelectGroup: "efa004Id",
					nameOfSelectPermission: "permission",
					nameOfSelectGroupDescription: "efa004Name",
					nameOfSelectUserDescription: "efa005Name",
					defaultValue: permissions
				}}
				customPermissionOptions={[
					{
						value: "2",
						label: translate("Visualiza")
					},
					{
						value: "1",
						label: translate("Visualiza e altera valores")
					}
				]}
			/>
		</Modal>
	);
};

export default React.forwardRef(ModalPermissionsFinancialManagement);
